<script setup lang="ts">
import { inject, Ref, ref } from 'vue';
import { DateTime } from 'luxon';
import { resetTimer } from '@/utils/dateUtils';
import cloneDeep from 'lodash/cloneDeep';
import { getCurrentLocale } from '@/i18n';
import { getTextVariant } from '@/utils/stringUtils';
import { useViewComposable } from '@/views/composable';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import Fluid from 'primevue/fluid';
import MultiSelect from 'primevue/multiselect';

const dialogRef = inject('dialogRef') as Ref;
const onSave = dialogRef?.value?.options?.emits?.onSave;
const onClose = dialogRef?.value?.options?.emits?.onClose;
const specialPromotionConfig = dialogRef?.value?.options?.data?.specialPromotionConfig;
const destinations = dialogRef?.value?.options?.data?.destinations;

const currentLanguage = getCurrentLocale();
const { brochureListOption } = useViewComposable();

const today = ref(new Date());
const isValidate = ref(true);

const formatData = (config: any) => {
  return config.map((item: any) => {
    return {
      expireDate: new Date(DateTime.fromISO(item.expireDate)),
      brochures: item?.brochures ? item?.brochures.split(',') : [],
      destinations: item?.destinations ? item?.destinations.split(',') : []
    };
  });
};

const specialPromotionConfigData = ref(cloneDeep(formatData(specialPromotionConfig)));

const addConfig = () => {
  specialPromotionConfigData.value.push({
    brochures: [],
    destinations: [],
    expireDate: undefined
  });
};

const deleteConfig = (indexDelete: number) => {
  specialPromotionConfigData.value = specialPromotionConfigData.value.filter((_: any, index: number) => index !== indexDelete);
};

const validateForm = () => {
  return specialPromotionConfigData.value.every((item: any) => {
    return (item.destinations.length > 0 || item.brochures.length > 0) && item.expireDate;
  });
};

const validateConfig = (config: any) => {
  return config.destinations.length || config.brochures.length;
};

const formatDataSubmit = (data: any) => {
  return data.map((item: any) => {
    return {
      ...item,
      expireDate: resetTimer(item.expireDate)
    };
  });
};

const onSaveConfigPromotion = () => {
  isValidate.value = validateForm();
  if (isValidate.value) {
    onSave(formatDataSubmit(specialPromotionConfigData.value));
    onClose();
  }
};
</script>
<template>
  <div class="manage-promotion-expiry">
    <p class="note-title text-base ml-1">{{ $t('management.quote_config.note') }}</p>
    <Fluid>
      <div v-if="specialPromotionConfigData?.length" class="grid mt-5">
        <p class="col-4 mb-0">{{ $t('management.package.brochure') }}</p>
        <p class="col-5 mb-0">{{ $t('management.package.destinations') }}</p>
        <p class="col-3 mb-0">{{ $t('worksheet.expiry_date') }}</p>
      </div>
      <div>
        <div class="grid field" v-for="(config, index) in specialPromotionConfigData" :key="index">
          <div class="col-4">
            <MultiSelect id="brochure-selected" display="chip" :maxSelectedLabels="3" :placeholder="$t('management.package.brochure')" v-model="config.brochures" :options="brochureListOption" class="w-full">
              <template #option="slotProps">
                <p>
                  {{ slotProps.option }}
                </p>
              </template>
            </MultiSelect>
            <p class="text-red-500 pt-1" v-if="!isValidate && !validateConfig(config)">{{ $t('validate.required') }}</p>
          </div>
          <div class="col-5">
            <MultiSelect
              id="destination-selected"
              filter
              :completeOnFocus="true"
              optionLabel="airportCode"
              optionValue="code"
              display="chip"
              :maxSelectedLabels="4"
              :placeholder="$t('common.destination')"
              v-model="config.destinations"
              :options="destinations"
              class="w-full"
            >
              <template #option="slotProps">
                <p>{{ slotProps.option.airportCode }} - {{ getTextVariant(currentLanguage, slotProps.option.name, slotProps.option.nameFr) }}</p>
              </template>
            </MultiSelect>
            <p class="text-red-500 pt-1" v-if="!isValidate && !validateConfig(config)">{{ $t('validate.required') }}</p>
          </div>
          <div class="col-3 flex gap-2">
            <div>
              <DatePicker :minDate="today" inputId="basic" v-model="config.expireDate" autocomplete="off" dateFormat="dd-M-yy" class="p-inputtext-sm" :placeholder="$t('common.date_format')">
                <template #footer>
                  <p class="text-center">
                    <small>{{ $t('common.press_space') }}</small>
                  </p>
                </template>
              </DatePicker>
              <p class="text-red-500 pt-1" v-if="!isValidate && !config.expireDate">{{ $t('validate.required') }}</p>
            </div>
            <div>
              <i class="remove-config-btn cursor-pointer pi pi-times-circle text-primary mt-3" @click="deleteConfig(index)"></i>
            </div>
          </div>
        </div>
      </div>
    </Fluid>
    <div class="text-center">
      <i class="add-config-btn cursor-pointer underline text-primary" @click="addConfig()">+ {{ $t('common.add') }}</i>
    </div>
    <div class="action-submit text-center">
      <Button id="submit-btn" :label="$t('common.buttons.submit')" class="mt-5" @click="onSaveConfigPromotion()" />
    </div>
  </div>
</template>
